import { AnimatePresence, motion, useInView } from "framer-motion"
import {
  ArrowRight,
  ArrowUpRight,
  BookOpen,
  Bot,
  Calendar,
  ChevronRight,
  FileAudio,
  Heart,
  MessageSquare,
  ScrollText,
  Search,
  Sparkles,
  Upload,
  Users,
  Wand2,
  Zap,
} from "lucide-react"
import { useCallback, useEffect, useId, useRef, useState } from "react"
import { AppLink } from "@/components/AppLink"
import { Translate } from "@/components/Translate"
import { WebsiteFooter } from "@/components/WebsiteFooter"
import { WebsiteHeader } from "@/components/WebsiteHeader"
import { useI18n } from "@/i18n"
import { usePalette } from "@/theme"

/* Palette colors are no longer declared here — they live in globals.css as
   CSS variables and are consumed via usePalette() from "@/theme". Arrays
   that previously referenced palette consts (TOUR_STEPS, FEATURE_KEYS,
   FLOATING_ORBS, COMMUNITY_FEATURES, SIGIL_BG) were inlined into their
   consumers below so they pick up theme changes on re-render. */

/* ───────── Constants ───────── */

const STATS = [
  { key: "sessions", value: "12,000+" },
  { key: "events", value: "450,000+" },
  { key: "hours", value: "8,500+" },
  { key: "campaigns", value: "2,400+" },
] as const

const COMMUNITY_URL =
  import.meta.env.VITE_COMMUNITY_URL || "https://community.tessel.app"
const API_URL =
  import.meta.env.VITE_API_URL ||
  "https://tessel-workers.tessel-api.workers.dev"

type CommunityCampaign = {
  short_id: string
  name: string
  description: string | null
  avatar_url: string | null
  session_count: number
  follower_count: number
  player_count: number
  last_session_date: string | null
}

function useCommuniyCampaigns() {
  const [campaigns, setCampaigns] = useState<CommunityCampaign[]>([])

  useEffect(() => {
    let cancelled = false

    fetch(`${API_URL}/api/public/campaigns`)
      .then((res) => {
        if (!res.ok) {
          throw new Error("Failed to fetch")
        }
        return res.json() as Promise<{ campaigns: CommunityCampaign[] }>
      })
      .then((data) => {
        if (!cancelled && data.campaigns.length > 0) {
          setCampaigns(data.campaigns)
        }
      })
      .catch(() => {
        // Keep fallback data
      })

    return () => {
      cancelled = true
    }
  }, [])

  return campaigns
}

function formatRelativeDate(
  dateStr: string | null,
  t: (key: string) => string,
): string {
  if (!dateStr) {
    return t("common.dates.empty")
  }
  const date = new Date(dateStr)
  const now = new Date()
  const diffDays = Math.floor(
    (now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24),
  )
  if (diffDays === 0) {
    return t("common.dates.today")
  }
  if (diffDays === 1) {
    return t("common.dates.yesterday")
  }
  if (diffDays < 7) {
    return `${diffDays}${t("common.dates.daysAgo")}`
  }
  if (diffDays < 30) {
    return `${Math.floor(diffDays / 7)}${t("common.dates.weeksAgo")}`
  }
  return `${Math.floor(diffDays / 30)}${t("common.dates.monthsAgo")}`
}

/* ───────── Runic Circle SVG ───────── */

function RunicCircle({ className = "" }: { className?: string }) {
  const pathId = useId()
  const { t } = useI18n()
  const { GOLD, RUNE_BLUE } = usePalette()

  return (
    <svg
      viewBox="0 0 400 400"
      className={`animate-[spin_60s_linear_infinite] ${className}`}
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={t("a11y.runicCircle")}
    >
      <title>{t("a11y.runicCircleTitle")}</title>
      <circle
        cx="200"
        cy="200"
        r="180"
        fill="none"
        stroke={GOLD}
        strokeWidth="0.5"
        opacity="0.15"
      />
      <circle
        cx="200"
        cy="200"
        r="150"
        fill="none"
        stroke={GOLD}
        strokeWidth="0.3"
        opacity="0.1"
        strokeDasharray="8 4"
      />
      <circle
        cx="200"
        cy="200"
        r="120"
        fill="none"
        stroke={RUNE_BLUE}
        strokeWidth="0.4"
        opacity="0.12"
      />
      {/* Rune marks around outer circle */}
      {Array.from({ length: 24 }).map((_, i) => {
        const angle = (i * 15 * Math.PI) / 180
        const x1 = 200 + 175 * Math.cos(angle)
        const y1 = 200 + 175 * Math.sin(angle)
        const x2 = 200 + 185 * Math.cos(angle)
        const y2 = 200 + 185 * Math.sin(angle)
        return (
          <line
            key={`rune-tick-${String(i)}`}
            x1={String(x1)}
            y1={String(y1)}
            x2={String(x2)}
            y2={String(y2)}
            stroke={GOLD}
            strokeWidth="0.6"
            opacity="0.2"
          />
        )
      })}
      {/* Inner rune text path */}
      <path
        id={pathId}
        d="M 200 200 m -130,0 a 130,130 0 1,1 260,0 a 130,130 0 1,1 -260,0"
        fill="none"
      />
      <text
        fill={GOLD}
        opacity="0.12"
        fontSize="10"
        fontFamily="serif"
        letterSpacing="6"
      >
        <textPath href={`#${pathId}`}>
          {
            "\u16A0\u16B1\u16C7\u16D6\u16A8\u16B7\u16C1\u16DA\u16A2\u16B9\u16CB\u16D2\u16A6\u16BE\u16C9\u16DE\u16A0\u16B1\u16C7\u16D6\u16A8\u16B7\u16C1\u16DA\u16A2\u16B9"
          }
        </textPath>
      </text>
      {/* Sigil cross lines */}
      <line
        x1="200"
        y1="50"
        x2="200"
        y2="350"
        stroke={GOLD}
        strokeWidth="0.2"
        opacity="0.06"
      />
      <line
        x1="50"
        y1="200"
        x2="350"
        y2="200"
        stroke={GOLD}
        strokeWidth="0.2"
        opacity="0.06"
      />
      <line
        x1="94"
        y1="94"
        x2="306"
        y2="306"
        stroke={RUNE_BLUE}
        strokeWidth="0.2"
        opacity="0.05"
      />
      <line
        x1="306"
        y1="94"
        x2="94"
        y2="306"
        stroke={RUNE_BLUE}
        strokeWidth="0.2"
        opacity="0.05"
      />
    </svg>
  )
}

/* ───────── Floating Orbs ───────── */

function FloatingOrbs() {
  const { GOLD, GOLD_LIGHT, RUNE_BLUE, EMERALD } = usePalette()
  // Defined inline so the orb colors pick up the active palette on theme
  // change. The literal "#c084fc" stays as-is — it is a spot accent, not a
  // palette token.
  const orbs = [
    { id: "orb1", top: "10%", left: "6%", size: 6, color: GOLD, delay: 0 },
    {
      id: "orb2",
      top: "25%",
      right: "10%",
      size: 4,
      color: RUNE_BLUE,
      delay: 1.2,
    },
    { id: "orb3", top: "50%", left: "4%", size: 5, color: EMERALD, delay: 2.5 },
    { id: "orb4", top: "70%", right: "7%", size: 3, color: GOLD, delay: 0.8 },
    {
      id: "orb5",
      top: "15%",
      left: "65%",
      size: 3.5,
      color: "#c084fc",
      delay: 3.2,
    },
    {
      id: "orb6",
      top: "80%",
      left: "35%",
      size: 4,
      color: RUNE_BLUE,
      delay: 1.6,
    },
    {
      id: "orb7",
      top: "40%",
      right: "20%",
      size: 2.5,
      color: GOLD_LIGHT,
      delay: 4.0,
    },
    {
      id: "orb8",
      top: "60%",
      left: "18%",
      size: 3,
      color: EMERALD,
      delay: 2.0,
    },
  ] as const

  return (
    <>
      {orbs.map((orb) => (
        <motion.div
          key={orb.id}
          className="absolute rounded-full pointer-events-none"
          style={{
            top: orb.top,
            left: "left" in orb ? orb.left : undefined,
            right: "right" in orb ? orb.right : undefined,
            width: orb.size,
            height: orb.size,
            backgroundColor: orb.color,
            boxShadow: `0 0 ${String(orb.size * 6)}px ${orb.color}80, 0 0 ${String(orb.size * 12)}px ${orb.color}40`,
          }}
          animate={{
            y: [0, -18, 0, 12, 0],
            opacity: [0.4, 0.9, 0.5, 1, 0.4],
            scale: [1, 1.4, 0.8, 1.2, 1],
          }}
          transition={{
            duration: 7 + orb.delay,
            repeat: Number.POSITIVE_INFINITY,
            delay: orb.delay,
            ease: "easeInOut" as const,
          }}
        />
      ))}
    </>
  )
}

/* ───────── Noise Texture Overlay ───────── */

function NoiseOverlay() {
  return (
    <div
      className="absolute inset-0 pointer-events-none opacity-[0.03] z-[1]"
      style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
        backgroundRepeat: "repeat",
        backgroundSize: "256px 256px",
      }}
    />
  )
}

/* ───────── Inscribing Hero Letters ───────── */

function InscribingText({
  text,
  className = "",
  goldGradient = false,
}: {
  text: string
  className?: string
  goldGradient?: boolean
}) {
  const { GOLD } = usePalette()
  return (
    <motion.span
      className={`inline-block ${className}`}
      initial="hidden"
      animate="visible"
      variants={{
        hidden: {},
        visible: {
          transition: {
            staggerChildren: 0.05,
          },
        },
      }}
    >
      {text.split("").map((char, i) => (
        <motion.span
          key={`${char}-${String(i)}`}
          className={`inline-block ${goldGradient ? "bg-gradient-to-r from-[#d4a853] via-[#e8c97a] to-[#d4a853] bg-clip-text text-transparent" : ""}`}
          variants={{
            hidden: {
              opacity: 0,
              y: 20,
              filter: "blur(8px)",
            },
            visible: {
              opacity: 1,
              y: 0,
              filter: "blur(0px)",
              transition: {
                duration: 0.4,
                ease: "easeOut" as const,
              },
            },
          }}
          style={
            goldGradient
              ? {
                  textShadow: `0 0 20px ${GOLD}60, 0 0 40px ${GOLD}30`,
                }
              : {
                  textShadow: `0 0 10px ${GOLD}30`,
                }
          }
        >
          {char === " " ? "\u00A0" : char}
        </motion.span>
      ))}
    </motion.span>
  )
}

/* ───────── Animated Section Wrapper ───────── */

function FadeInSection({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode
  className?: string
  delay?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const isInView = useInView(ref, { once: true, margin: "-60px" })

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{
        duration: 0.9,
        delay,
        ease: [0.25, 0.1, 0.25, 1],
      }}
      className={className}
    >
      {children}
    </motion.div>
  )
}

/* ───────── Scroll Unfurling Tour Step ───────── */

function ScrollUnfurlContent({
  isActive,
  stepKey,
  color,
}: {
  isActive: boolean
  stepKey: string
  color: string
}) {
  const { GOLD, PARCHMENT } = usePalette()
  const [displayedText, setDisplayedText] = useState("")
  const { t } = useI18n()
  const fullText = t(`tour.steps.${stepKey}.demo`)

  useEffect(() => {
    if (!isActive) {
      setDisplayedText("")
      return
    }

    let index = 0
    setDisplayedText("")

    const interval = setInterval(() => {
      if (index < fullText.length) {
        setDisplayedText(fullText.slice(0, index + 1))
        index++
      } else {
        clearInterval(interval)
      }
    }, 25)

    return () => clearInterval(interval)
  }, [isActive, fullText])

  return (
    <AnimatePresence>
      {isActive && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{
            height: { duration: 0.8, ease: [0.4, 0, 0.2, 1] },
            opacity: { duration: 0.5, delay: 0.15 },
          }}
          style={{
            clipPath: isActive ? "inset(0 0 0 0)" : "inset(0 0 100% 0)",
          }}
          className="overflow-hidden"
        >
          <div
            className="p-6 rounded-xl border mt-4"
            style={{
              backgroundColor: `${color}08`,
              borderColor: `${color}25`,
            }}
          >
            <div
              className="flex items-center gap-2 mb-3 text-xs font-mono"
              style={{ color }}
            >
              <div
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: color }}
              />
              <Translate k={`tour.steps.${stepKey}.title`} />
            </div>
            <p
              className="font-mono text-sm leading-relaxed"
              style={{ color: PARCHMENT }}
            >
              {displayedText}
              {displayedText.length < fullText.length && (
                <motion.span
                  animate={{ opacity: [1, 0] }}
                  transition={{
                    duration: 0.5,
                    repeat: Number.POSITIVE_INFINITY,
                    repeatType: "reverse",
                  }}
                  style={{ color: GOLD }}
                >
                  |
                </motion.span>
              )}
            </p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/* ───────── Palantir Scrying Pool ───────── */

const CHAT_DURATIONS = [2500, 4000, 3000] as const

function ScryingPool() {
  const [chatPhase, setChatPhase] = useState(0)
  const { t } = useI18n()
  const { CONTENT_BG, GOLD, RUNE_BLUE, EMERALD, PARCHMENT } = usePalette()

  useEffect(() => {
    const timeout = setTimeout(() => {
      setChatPhase((p) => (p + 1) % 3)
    }, CHAT_DURATIONS[chatPhase])
    return () => clearTimeout(timeout)
  }, [chatPhase])

  return (
    <div className="relative max-w-lg mx-auto">
      {/* Pool outer ring */}
      <div
        className="absolute -inset-4 rounded-full opacity-20 blur-xl"
        style={{
          background: `radial-gradient(circle, ${RUNE_BLUE}40, ${GOLD}20, transparent)`,
        }}
      />

      {/* Pool container */}
      <div
        className="relative rounded-3xl p-6 border overflow-hidden"
        style={{
          backgroundColor: `${CONTENT_BG}ee`,
          borderColor: `${GOLD}30`,
          boxShadow: `0 0 40px ${GOLD}10, inset 0 0 60px ${RUNE_BLUE}08`,
        }}
      >
        {/* Ripple effect background */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border"
            style={{ borderColor: `${RUNE_BLUE}15` }}
            animate={{
              width: [0, 400],
              height: [0, 400],
              opacity: [0.4, 0],
            }}
            transition={{
              duration: 3,
              repeat: Number.POSITIVE_INFINITY,
              ease: "easeOut" as const,
            }}
          />
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border"
            style={{ borderColor: `${GOLD}10` }}
            animate={{
              width: [0, 300],
              height: [0, 300],
              opacity: [0.3, 0],
            }}
            transition={{
              duration: 3,
              repeat: Number.POSITIVE_INFINITY,
              delay: 1.5,
              ease: "easeOut" as const,
            }}
          />
        </div>

        {/* Header */}
        <div
          className="relative flex items-center gap-3 mb-5 pb-4 border-b"
          style={{ borderColor: `${GOLD}15` }}
        >
          <div
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ backgroundColor: `${RUNE_BLUE}20` }}
          >
            <Bot className="h-5 w-5" style={{ color: RUNE_BLUE }} />
          </div>
          <div>
            <div className="font-semibold text-sm" style={{ color: PARCHMENT }}>
              {t("palantir.chat.name")}
            </div>
            <div className="text-xs" style={{ color: `${GOLD}80` }}>
              {t("palantir.chat.role")}
            </div>
          </div>
          <div className="ml-auto flex items-center gap-1.5">
            <div
              className="w-2 h-2 rounded-full animate-pulse"
              style={{ backgroundColor: EMERALD }}
            />
            <span className="text-xs" style={{ color: EMERALD }}>
              {t("palantir.chat.online")}
            </span>
          </div>
        </div>

        {/* Messages */}
        <motion.div
          className="relative space-y-3 min-h-[120px]"
          layout
          transition={{ layout: { duration: 0.4, ease: "easeInOut" } }}
        >
          <AnimatePresence mode="wait">
            {chatPhase >= 0 && (
              <motion.div
                key="user-msg"
                initial={{ opacity: 0, x: 20, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
                className="rounded-xl p-3 ml-auto max-w-[80%]"
                style={{
                  backgroundColor: `${GOLD}12`,
                  border: `1px solid ${GOLD}20`,
                }}
              >
                <p className="text-sm" style={{ color: PARCHMENT }}>
                  {t("palantir.chat.userMessage")}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {chatPhase >= 1 && (
              <motion.div
                key="bot-msg"
                initial={{ opacity: 0, x: -20, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.4, delay: 0.3, ease: "easeOut" }}
                layout
                className="rounded-xl p-3 max-w-[90%]"
                style={{
                  backgroundColor: `${RUNE_BLUE}10`,
                  border: `1px solid ${RUNE_BLUE}20`,
                }}
              >
                <p className="text-sm" style={{ color: PARCHMENT }}>
                  {t("palantir.chat.botResponse")}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {chatPhase >= 2 && (
              <motion.div
                key="scrying"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="flex items-center gap-2 text-xs"
                style={{ color: `${GOLD}80` }}
              >
                <Sparkles className="h-3 w-3" style={{ color: GOLD }} />
                <span>{t("palantir.chat.scrying")}</span>
                <motion.span
                  animate={{ opacity: [0.3, 1, 0.3] }}
                  transition={{
                    duration: 1.5,
                    repeat: Number.POSITIVE_INFINITY,
                  }}
                  style={{ color: GOLD }}
                >
                  ●●●
                </motion.span>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  )
}

/* ───────── Torn Edge SVG Divider ───────── */

/**
 * SVG-based torn edge. Placed at the BOTTOM of each section.
 * Fills with the NEXT section's color, creating teeth that bite
 * into the current section. Works with any background (gradients, images, etc).
 */
function TornEdge({ color }: { color: string }) {
  const { t } = useI18n()

  return (
    <div className="absolute bottom-0 left-0 right-0 pointer-events-none z-[2]">
      <svg
        viewBox="0 0 1440 40"
        preserveAspectRatio="none"
        className="block w-full h-8 md:h-10"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <title>{t("a11y.sectionDivider")}</title>
        <path
          d="M0,40 L0,20 L40,38 L80,15 L120,35 L160,12 L200,32 L240,18 L280,38 L320,10 L360,30 L400,18 L440,36 L480,14 L520,34 L560,16 L600,38 L640,12 L680,32 L720,20 L760,36 L800,14 L840,34 L880,18 L920,38 L960,10 L1000,30 L1040,18 L1080,36 L1120,12 L1160,34 L1200,16 L1240,38 L1280,14 L1320,32 L1360,18 L1400,36 L1440,20 L1440,40 Z"
          fill={color}
        />
      </svg>
    </div>
  )
}

/* ───────── Main Component ───────── */

export const WebsiteVersionG = () => {
  const [activeTourStep, setActiveTourStep] = useState(0)
  const { t, tArray } = useI18n()
  const communityCampaigns = useCommuniyCampaigns()
  const {
    DEEP_INDIGO,
    CONTENT_BG,
    CONTENT_ALT,
    GOLD,
    GOLD_LIGHT,
    RUNE_BLUE,
    EMERALD,
    PARCHMENT,
  } = usePalette()

  // Arrays that reference palette colors are defined inside the component so
  // a theme switch produces fresh entries on re-render. Non-palette literals
  // ("#c084fc", "#f472b6") are spot accents that stay constant across themes.
  const TOUR_STEPS = [
    { key: "upload", icon: Upload, color: RUNE_BLUE, rune: "ᚠ" },
    { key: "transcribe", icon: FileAudio, color: GOLD, rune: "ᚱ" },
    { key: "extract", icon: Sparkles, color: EMERALD, rune: "ᛇ" },
    { key: "summarize", icon: ScrollText, color: "#c084fc", rune: "ᛖ" },
    { key: "ask", icon: Bot, color: "#f472b6", rune: "ᚨ" },
  ] as const

  const FEATURE_KEYS = [
    { key: "transcription", icon: FileAudio, color: RUNE_BLUE },
    { key: "summaries", icon: ScrollText, color: GOLD },
    { key: "events", icon: Sparkles, color: EMERALD },
    { key: "palantir", icon: Bot, color: "#c084fc" },
    { key: "collaboration", icon: Users, color: "#f472b6" },
    { key: "archive", icon: Wand2, color: GOLD_LIGHT },
  ] as const

  const COMMUNITY_FEATURES = [
    { key: "discover", icon: Search, color: RUNE_BLUE },
    { key: "follow", icon: BookOpen, color: GOLD },
    { key: "engage", icon: Heart, color: EMERALD },
  ] as const

  const SIGIL_BG = {
    backgroundImage: `radial-gradient(circle, ${GOLD}05 1px, transparent 1px)`,
    backgroundSize: "32px 32px",
  } as const

  const handleTourStep = useCallback((idx: number) => {
    setActiveTourStep(idx)
  }, [])

  const currentStep = TOUR_STEPS[activeTourStep]
  const tourStepCount = TOUR_STEPS.length

  /* ── Auto-advance tour ── */
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTourStep((prev) => (prev + 1) % tourStepCount)
    }, 8000)
    return () => clearInterval(interval)
  }, [tourStepCount])

  return (
    <div className="min-h-screen" style={{ backgroundColor: DEEP_INDIGO }}>
      <NoiseOverlay />

      <WebsiteHeader />

      {/* ===== Hero ===== */}
      <section
        className="min-h-screen flex flex-col justify-center pb-16 px-6 lg:px-16 relative overflow-hidden"
        style={{
          backgroundColor: DEEP_INDIGO,
        }}
      >
        {/* Subtle gradient glow — contained to center to avoid torn edge bleed */}
        <div
          className="absolute inset-x-0 top-[10%] bottom-[15%]"
          style={{
            background: `radial-gradient(ellipse at 50% 40%, ${GOLD}06, transparent 50%)`,
          }}
        />

        {/* Runic circle - centered, large, rotating */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] lg:w-[900px] lg:h-[900px] pointer-events-none">
          <RunicCircle />
        </div>

        {/* Sigil watermark */}
        <div
          className="absolute top-1/4 right-10 text-[180px] font-serif pointer-events-none select-none"
          style={{ color: `${GOLD}04` }}
        >
          {"\u16A0"}
        </div>

        <FloatingOrbs />

        <div className="relative z-10 max-w-6xl mx-auto w-full text-center">
          <FadeInSection>
            {/* Gold shimmer light pulse */}
            <motion.div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full pointer-events-none"
              style={{
                background: `radial-gradient(circle, ${GOLD}15, transparent 70%)`,
              }}
              animate={{
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.6, 0.3],
              }}
              transition={{
                duration: 4,
                repeat: Number.POSITIVE_INFINITY,
                ease: "easeInOut" as const,
              }}
            />

            <h1
              className="text-5xl md:text-6xl lg:text-7xl font-black leading-[1.1] mb-6"
              style={{ color: PARCHMENT }}
            >
              <InscribingText text={t("hero.title1")} />
              <br />
              <InscribingText text={t("hero.title2")} />
              <br />
              <InscribingText text={t("hero.title3")} goldGradient />
            </h1>

            <motion.p
              className="text-xl max-w-2xl mx-auto mb-10 leading-relaxed"
              style={{ color: `${PARCHMENT}80` }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8, duration: 0.6 }}
            >
              <Translate k="hero.description" />
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 2.2, duration: 0.6 }}
            >
              <AppLink
                href="/signup"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold text-lg transition-all cursor-pointer hover:scale-105"
                style={{
                  background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
                  color: DEEP_INDIGO,
                  boxShadow: `0 0 30px ${GOLD}40`,
                }}
              >
                <Translate k="hero.cta" />
                <ArrowRight className="h-5 w-5" />
              </AppLink>
              <AppLink
                href="/login"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 font-semibold text-lg transition-colors cursor-pointer"
                style={{ color: `${PARCHMENT}80` }}
              >
                <Translate k="hero.haveAccount" />
                <ArrowUpRight className="h-5 w-5" />
              </AppLink>
            </motion.div>
          </FadeInSection>
        </div>
        <TornEdge color={CONTENT_BG} />
      </section>
      {/* ===== Interactive Tour (Scroll Unfurling) ===== */}
      {/* biome-ignore lint/correctness/useUniqueElementIds: anchor navigation requires static IDs */}
      <section
        id="tour"
        className="py-24 px-6 lg:px-16 relative overflow-hidden"
        style={{ backgroundColor: CONTENT_BG }}
      >
        {/* Sigil watermarks */}
        <div
          className="absolute top-10 left-10 text-[120px] font-serif pointer-events-none select-none"
          style={{ color: `${GOLD}03` }}
        >
          {"\u16B7"}
        </div>
        <div
          className="absolute bottom-10 right-10 text-[100px] font-serif pointer-events-none select-none"
          style={{ color: `${RUNE_BLUE}03` }}
        >
          {"\u16C1"}
        </div>

        <div className="relative z-10 max-w-6xl mx-auto">
          <FadeInSection className="text-center mb-16">
            <h2
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ color: PARCHMENT }}
            >
              <Translate k="tour.title" />{" "}
              <span
                style={{
                  backgroundImage: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textShadow: "none",
                }}
              >
                <Translate k="tour.titleHighlight" />
              </span>
            </h2>
            <p
              className="text-xl max-w-2xl mx-auto"
              style={{ color: `${PARCHMENT}60` }}
            >
              <Translate k="tour.description" />
            </p>
          </FadeInSection>

          <FadeInSection delay={0.1}>
            <div className="grid lg:grid-cols-[280px_1fr] gap-12 items-start">
              {/* Rune Steps (left sidebar) */}
              <div className="flex lg:flex-col gap-3 overflow-x-auto lg:overflow-visible pb-4 lg:pb-0">
                {TOUR_STEPS.map((step, idx) => {
                  const Icon = step.icon
                  const isActive = idx === activeTourStep
                  return (
                    <motion.button
                      key={step.key}
                      type="button"
                      onClick={() => handleTourStep(idx)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium cursor-pointer border min-w-[200px] lg:min-w-0 relative"
                      animate={{
                        backgroundColor: isActive
                          ? `${step.color}15`
                          : "rgba(0,0,0,0)",
                        borderColor: isActive ? `${step.color}30` : `${GOLD}10`,
                        boxShadow: isActive
                          ? `0 0 20px ${step.color}15`
                          : "0 0 0px transparent",
                      }}
                      transition={{ duration: 0.5, ease: "easeInOut" as const }}
                    >
                      <motion.span
                        animate={{
                          color: isActive ? step.color : `${PARCHMENT}50`,
                        }}
                        transition={{ duration: 0.5 }}
                      >
                        <Icon className="h-4 w-4 flex-shrink-0" />
                      </motion.span>
                      <motion.span
                        className="hidden sm:inline whitespace-nowrap"
                        animate={{
                          color: isActive ? PARCHMENT : `${PARCHMENT}60`,
                        }}
                        transition={{ duration: 0.5 }}
                      >
                        <Translate k={`tour.steps.${step.key}.label`} />
                      </motion.span>

                      {/* Connecting line to next step */}
                      {idx < TOUR_STEPS.length - 1 && (
                        <motion.div
                          className="hidden lg:block absolute -bottom-3 left-1/2 -translate-x-1/2 w-px h-3"
                          animate={{
                            backgroundColor:
                              idx < activeTourStep ? `${GOLD}30` : `${GOLD}10`,
                          }}
                          transition={{ duration: 0.5 }}
                        />
                      )}
                    </motion.button>
                  )
                })}
              </div>

              {/* Scroll content area (right) */}
              <div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeTourStep}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{
                      duration: 0.5,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                  >
                    <motion.div
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium mb-4"
                      animate={{
                        backgroundColor: `${currentStep.color}15`,
                        color: currentStep.color,
                      }}
                      transition={{ duration: 0.5 }}
                    >
                      <currentStep.icon className="h-3.5 w-3.5" />
                      {t("tour.stepLabel")} {String(activeTourStep + 1)} /{" "}
                      {String(TOUR_STEPS.length)}
                    </motion.div>

                    <h3
                      className="text-3xl md:text-4xl font-bold mb-4"
                      style={{ color: PARCHMENT }}
                    >
                      <Translate k={`tour.steps.${currentStep.key}.title`} />
                    </h3>

                    <p
                      className="text-lg leading-relaxed mb-4"
                      style={{ color: `${PARCHMENT}70` }}
                    >
                      <Translate
                        k={`tour.steps.${currentStep.key}.description`}
                      />
                    </p>

                    {activeTourStep < TOUR_STEPS.length - 1 && (
                      <button
                        type="button"
                        onClick={() => handleTourStep(activeTourStep + 1)}
                        className="inline-flex items-center gap-2 text-sm font-medium transition-colors cursor-pointer mb-4"
                        style={{ color: currentStep.color }}
                      >
                        {t("tour.nextStep")}
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    )}
                  </motion.div>
                </AnimatePresence>

                {/* Scroll unfurling demo area */}
                <motion.div
                  className="rounded-2xl border p-6"
                  animate={{
                    borderColor: `${currentStep.color}18`,
                    boxShadow: `inset 0 0 40px ${currentStep.color}05`,
                  }}
                  transition={{ duration: 0.8, ease: "easeInOut" as const }}
                  style={{
                    backgroundColor: `${CONTENT_BG}cc`,
                  }}
                >
                  <div
                    className="flex items-center gap-2 mb-2 text-xs font-mono"
                    style={{ color: `${GOLD}60` }}
                  >
                    <motion.div
                      className="w-2 h-2 rounded-full animate-pulse"
                      animate={{ backgroundColor: currentStep.color }}
                      transition={{ duration: 0.5 }}
                    />
                    {t("tour.grimoirePreview")}
                  </div>
                  {TOUR_STEPS.map((step, idx) => (
                    <ScrollUnfurlContent
                      key={step.key}
                      isActive={idx === activeTourStep}
                      stepKey={step.key}
                      color={step.color}
                    />
                  ))}

                  {/* Step progress runes */}
                  <div className="flex gap-3 mt-6 justify-center">
                    {TOUR_STEPS.map((step, idx) => (
                      <motion.button
                        key={step.key}
                        type="button"
                        onClick={() => handleTourStep(idx)}
                        className="cursor-pointer text-lg font-serif"
                        animate={{
                          color:
                            idx === activeTourStep
                              ? currentStep.color
                              : `${GOLD}25`,
                          textShadow:
                            idx === activeTourStep
                              ? `0 0 10px ${currentStep.color}60`
                              : "0 0 0px transparent",
                          scale: idx === activeTourStep ? 1.3 : 1,
                        }}
                        transition={{ duration: 0.5, ease: "easeOut" as const }}
                      >
                        {step.rune}
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              </div>
            </div>
          </FadeInSection>
        </div>
        <TornEdge color={CONTENT_ALT} />
      </section>
      {/* ===== Features Grid ===== */}
      {/* biome-ignore lint/correctness/useUniqueElementIds: anchor navigation requires static IDs */}
      <section
        id="features"
        className="py-24 px-6 lg:px-16 relative"
        style={{ backgroundColor: CONTENT_ALT }}
      >
        {/* Faint runic circle in background */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] opacity-30 pointer-events-none">
          <RunicCircle />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto">
          <FadeInSection className="text-center mb-16">
            <h2
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ color: PARCHMENT }}
            >
              <Translate k="features.title" />{" "}
              <span
                style={{
                  backgroundImage: `linear-gradient(135deg, ${GOLD}, ${EMERALD})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textShadow: "none",
                }}
              >
                <Translate k="features.titleHighlight" />
              </span>
            </h2>
            <p
              className="text-xl max-w-2xl mx-auto"
              style={{ color: `${PARCHMENT}60` }}
            >
              <Translate k="features.description" />
            </p>
          </FadeInSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURE_KEYS.map(({ key, icon: Icon, color }, idx) => (
              <FadeInSection key={key} delay={idx * 0.1}>
                <motion.div
                  className="group p-7 rounded-2xl border h-full relative overflow-hidden"
                  style={{
                    backgroundColor: `${CONTENT_BG}cc`,
                  }}
                  initial={{
                    borderColor: `${GOLD}10`,
                    boxShadow: "0 0 0px transparent",
                  }}
                  whileHover={{
                    borderColor: `${color}30`,
                    boxShadow: `0 0 30px ${color}10`,
                  }}
                  transition={{ duration: 0.4, ease: "easeOut" as const }}
                >
                  {/* Subtle sigil watermark */}
                  <div
                    className="absolute -top-4 -right-4 text-[80px] font-serif pointer-events-none select-none"
                    style={{ color: `${color}06` }}
                  >
                    {"\u16A0"}
                  </div>

                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110"
                    style={{ backgroundColor: `${color}15` }}
                  >
                    <Icon className="h-6 w-6" style={{ color }} />
                  </div>
                  <h3
                    className="text-lg font-semibold mb-2"
                    style={{ color: PARCHMENT }}
                  >
                    <Translate k={`features.items.${key}.title`} />
                  </h3>
                  <p
                    className="text-sm leading-relaxed"
                    style={{ color: `${PARCHMENT}60` }}
                  >
                    <Translate k={`features.items.${key}.description`} />
                  </p>
                </motion.div>
              </FadeInSection>
            ))}
          </div>
        </div>
        <TornEdge color={CONTENT_BG} />
      </section>
      {/* ===== How It Works ===== */}
      {/* biome-ignore lint/correctness/useUniqueElementIds: anchor navigation requires static IDs */}
      <section
        id="how-it-works"
        className="py-24 px-6 lg:px-16 relative overflow-hidden"
        style={{ backgroundColor: CONTENT_BG }}
      >
        <FloatingOrbs />

        <div className="relative z-10 max-w-6xl mx-auto">
          <FadeInSection className="text-center mb-16">
            <h2
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ color: PARCHMENT }}
            >
              <Translate k="howItWorks.title" />{" "}
              <span
                style={{
                  backgroundImage: `linear-gradient(135deg, ${GOLD}, ${RUNE_BLUE})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textShadow: "none",
                }}
              >
                <Translate k="howItWorks.titleHighlight" />
              </span>
            </h2>
            <p
              className="text-xl max-w-2xl mx-auto"
              style={{ color: `${PARCHMENT}60` }}
            >
              <Translate k="howItWorks.description" />
            </p>
          </FadeInSection>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                key: "record",
                icon: FileAudio,
                color: RUNE_BLUE,
                rune: "\u16A0",
              },
              { key: "process", icon: Zap, color: GOLD, rune: "\u16B1" },
              {
                key: "explore",
                icon: MessageSquare,
                color: EMERALD,
                rune: "\u16C7",
              },
            ].map(({ key, icon: Icon, color, rune }, idx) => (
              <FadeInSection key={key} delay={idx * 0.15}>
                <div className="relative text-center">
                  {/* Large rune number background */}
                  <div
                    className="text-[120px] font-serif leading-none mb-2 select-none"
                    style={{
                      color: `${color}10`,
                      textShadow: `0 0 40px ${color}08`,
                    }}
                  >
                    {rune}
                  </div>

                  <div className="relative -mt-16">
                    <div
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-4 mx-auto"
                      style={{
                        backgroundColor: `${color}15`,
                        boxShadow: `0 0 20px ${color}15`,
                      }}
                    >
                      <Icon className="h-7 w-7" style={{ color }} />
                    </div>
                    <div
                      className="text-xs font-mono mb-2"
                      style={{ color: `${color}80` }}
                    >
                      {String(idx + 1).padStart(2, "0")}
                    </div>
                    <h3
                      className="text-2xl font-semibold mb-3"
                      style={{ color: PARCHMENT }}
                    >
                      <Translate k={`howItWorks.steps.${key}.title`} />
                    </h3>
                    <p
                      className="leading-relaxed"
                      style={{ color: `${PARCHMENT}60` }}
                    >
                      <Translate k={`howItWorks.steps.${key}.description`} />
                    </p>
                  </div>

                  {/* Connector line between steps */}
                  {idx < 2 && (
                    <div
                      className="hidden md:block absolute top-1/3 -right-4 w-8 h-px"
                      style={{ backgroundColor: `${GOLD}20` }}
                    />
                  )}
                </div>
              </FadeInSection>
            ))}
          </div>
        </div>
        <TornEdge color={CONTENT_ALT} />
      </section>
      {/* ===== Community Section ===== */}
      <section
        className="py-24 px-6 lg:px-16 relative overflow-hidden"
        style={{ backgroundColor: CONTENT_ALT }}
      >
        <FloatingOrbs />

        <div className="relative z-10 max-w-6xl mx-auto">
          <FadeInSection className="text-center mb-16">
            <h2
              className="text-4xl md:text-5xl font-bold mb-4"
              style={{ color: PARCHMENT }}
            >
              <Translate k="community.title" />{" "}
              <span
                style={{
                  backgroundImage: `linear-gradient(135deg, ${GOLD}, ${EMERALD})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textShadow: "none",
                }}
              >
                <Translate k="community.titleHighlight" />
              </span>
            </h2>
            <p
              className="text-xl max-w-2xl mx-auto"
              style={{ color: `${PARCHMENT}60` }}
            >
              <Translate k="community.description" />
            </p>
          </FadeInSection>

          {/* Community feature bullets */}
          <div className="grid md:grid-cols-3 gap-6 mb-16">
            {COMMUNITY_FEATURES.map(({ key, icon: Icon, color }, idx) => (
              <FadeInSection key={key} delay={idx * 0.1}>
                <div
                  className="flex items-start gap-4 p-5 rounded-xl"
                  style={{
                    backgroundColor: `${color}08`,
                    border: `1px solid ${color}15`,
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: `${color}15`,
                    }}
                  >
                    <Icon className="h-5 w-5" style={{ color }} />
                  </div>
                  <div>
                    <h3
                      className="font-semibold mb-1"
                      style={{ color: PARCHMENT }}
                    >
                      <Translate k={`community.features.${key}.title`} />
                    </h3>
                    <p
                      className="text-sm leading-relaxed"
                      style={{ color: `${PARCHMENT}60` }}
                    >
                      <Translate k={`community.features.${key}.description`} />
                    </p>
                  </div>
                </div>
              </FadeInSection>
            ))}
          </div>

          {/* Campaign cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {communityCampaigns.map((campaign, idx) => (
              <FadeInSection key={campaign.short_id} delay={idx * 0.12}>
                <a
                  href={`${COMMUNITY_URL}/${campaign.short_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group"
                >
                  <div
                    className="relative rounded-xl p-6 h-full transition-all duration-300"
                    style={{
                      backgroundColor: `${CONTENT_BG}`,
                      border: `1px solid ${GOLD}15`,
                      boxShadow: `0 0 0 0 ${GOLD}00`,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = `${GOLD}40`
                      e.currentTarget.style.boxShadow = `0 0 24px ${GOLD}10`
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = `${GOLD}15`
                      e.currentTarget.style.boxShadow = `0 0 0 0 ${GOLD}00`
                    }}
                  >
                    {/* Campaign name */}
                    <h4
                      className="text-lg font-semibold mb-2 group-hover:opacity-90 transition-opacity"
                      style={{ color: PARCHMENT }}
                    >
                      {campaign.name}
                    </h4>

                    {/* Description truncated */}
                    <p
                      className="text-sm leading-relaxed mb-4 line-clamp-2"
                      style={{ color: `${PARCHMENT}50` }}
                    >
                      {campaign.description || "—"}
                    </p>

                    {/* Stats row */}
                    <div
                      className="flex items-center gap-4 text-xs mb-3"
                      style={{ color: `${PARCHMENT}50` }}
                    >
                      <span className="flex items-center gap-1">
                        <BookOpen
                          className="h-3.5 w-3.5"
                          style={{ color: GOLD }}
                        />
                        {campaign.session_count} {t("community.card.sessions")}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users
                          className="h-3.5 w-3.5"
                          style={{ color: RUNE_BLUE }}
                        />
                        {campaign.follower_count}{" "}
                        {t("community.card.followers")}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users
                          className="h-3.5 w-3.5"
                          style={{ color: EMERALD }}
                        />
                        {campaign.player_count} {t("community.card.players")}
                      </span>
                    </div>

                    {/* Last session */}
                    <div
                      className="flex items-center gap-1 text-xs"
                      style={{ color: `${PARCHMENT}40` }}
                    >
                      <Calendar className="h-3 w-3" />
                      {t("community.card.lastSession")}:{" "}
                      {formatRelativeDate(campaign.last_session_date, t)}
                    </div>

                    {/* Arrow indicator on hover */}
                    <div
                      className="absolute top-5 right-5 opacity-0 group-hover:opacity-100 transition-opacity"
                      style={{ color: GOLD }}
                    >
                      <ArrowUpRight className="h-4 w-4" />
                    </div>
                  </div>
                </a>
              </FadeInSection>
            ))}
          </div>

          {/* CTA */}
          <FadeInSection className="text-center">
            <a
              href={COMMUNITY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3 rounded-full font-medium transition-all duration-300 hover:scale-105"
              style={{
                backgroundColor: `${GOLD}15`,
                color: GOLD,
                border: `1px solid ${GOLD}30`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${GOLD}25`
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = `${GOLD}15`
              }}
            >
              <Translate k="community.cta" />
              <ArrowRight className="h-4 w-4" />
            </a>
          </FadeInSection>
        </div>
        <TornEdge color={CONTENT_BG} />
      </section>
      {/* ===== Palantir Section (Scrying Pool) ===== */}
      <section
        className="py-24 px-6 lg:px-16 relative overflow-hidden"
        style={{ backgroundColor: CONTENT_BG }}
      >
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[200px]"
          style={{ backgroundColor: `${RUNE_BLUE}08` }}
        />

        <FloatingOrbs />

        <div className="relative z-10 max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <FadeInSection>
              <div
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6"
                style={{
                  backgroundColor: `${RUNE_BLUE}15`,
                  color: RUNE_BLUE,
                }}
              >
                <Bot className="h-4 w-4" />
                <Translate k="palantir.badge" />
              </div>
              <h2
                className="text-4xl md:text-5xl font-bold mb-6"
                style={{ color: PARCHMENT }}
              >
                <Translate k="palantir.title" />{" "}
                <span
                  style={{
                    backgroundImage: `linear-gradient(135deg, ${RUNE_BLUE}, ${GOLD})`,
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    textShadow: "none",
                  }}
                >
                  <Translate k="palantir.titleHighlight" />
                </span>
              </h2>
              <p
                className="text-xl mb-8 leading-relaxed"
                style={{ color: `${PARCHMENT}70` }}
              >
                <Translate k="palantir.description" />
              </p>
              <ul className="space-y-4">
                {tArray("palantir.features").map((item) => (
                  <li key={item} className="flex items-start gap-3">
                    <div
                      className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ backgroundColor: `${GOLD}20` }}
                    >
                      <Sparkles
                        className="h-3.5 w-3.5"
                        style={{ color: GOLD }}
                      />
                    </div>
                    <span style={{ color: `${PARCHMENT}90` }}>{item}</span>
                  </li>
                ))}
              </ul>
            </FadeInSection>

            <FadeInSection delay={0.2}>
              <ScryingPool />
            </FadeInSection>
          </div>
        </div>
        <TornEdge color={CONTENT_BG} />
      </section>
      {/* ===== Stats ===== */}
      <section
        className="py-16 px-6 lg:px-16 relative"
        style={{ backgroundColor: CONTENT_BG }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(90deg, transparent, ${GOLD}05, transparent)`,
          }}
        />

        <div className="relative z-10 max-w-5xl mx-auto">
          <FadeInSection>
            <div
              className="grid grid-cols-2 md:grid-cols-4 gap-8 py-8 px-6 rounded-2xl border"
              style={{
                backgroundColor: `${CONTENT_ALT}cc`,
                borderColor: `${GOLD}12`,
                boxShadow: `0 0 40px ${GOLD}05`,
              }}
            >
              {STATS.map(({ key, value }) => (
                <div key={key} className="text-center">
                  <div
                    className="text-3xl md:text-4xl font-bold mb-1"
                    style={{
                      backgroundImage: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      textShadow: "none",
                    }}
                  >
                    {value}
                  </div>
                  <div className="text-sm" style={{ color: `${PARCHMENT}50` }}>
                    <Translate k={`stats.${key}`} />
                  </div>
                </div>
              ))}
            </div>
          </FadeInSection>
        </div>
        <TornEdge color={CONTENT_ALT} />
      </section>
      {/* ===== CTA ===== */}
      <section
        className="py-24 px-6 lg:px-16 relative overflow-hidden"
        style={{
          ...SIGIL_BG,
          backgroundColor: CONTENT_ALT,
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at center, ${GOLD}08, transparent 60%)`,
          }}
        />

        {/* Large runic circle behind CTA */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-40 pointer-events-none">
          <RunicCircle />
        </div>

        <FloatingOrbs />

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <FadeInSection>
            <h2
              className="text-4xl md:text-5xl font-bold mb-6"
              style={{
                color: PARCHMENT,
                textShadow: `0 0 30px ${GOLD}20`,
              }}
            >
              <Translate k="cta.title" />
            </h2>
            <p
              className="text-xl mb-8 max-w-2xl mx-auto"
              style={{ color: `${PARCHMENT}70` }}
            >
              <Translate k="cta.description" />
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <AppLink
                href="/signup"
                className="flex items-center gap-2 px-8 py-4 rounded-full font-semibold text-lg transition-all cursor-pointer hover:scale-105"
                style={{
                  background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`,
                  color: DEEP_INDIGO,
                  boxShadow: `0 0 30px ${GOLD}40`,
                }}
              >
                <Translate k="cta.button" />
                <ArrowRight className="h-5 w-5" />
              </AppLink>
              <AppLink
                href="/login"
                className="flex items-center gap-2 border px-8 py-4 rounded-full font-semibold text-lg transition-all cursor-pointer"
                style={{
                  borderColor: `${GOLD}40`,
                  color: PARCHMENT,
                }}
              >
                <Translate k="cta.haveAccount" />
              </AppLink>
            </div>
          </FadeInSection>
        </div>
        <TornEdge color={DEEP_INDIGO} />
      </section>
      {/* ===== Footer ===== */}
      <WebsiteFooter />
    </div>
  )
}
